import { create } from "zustand";
import * as Sentry from "@sentry/react";
import {
  getOnboardingState,
  saveOnboardingState,
  saveOnboardingStateDebounced,
} from "./api";
import {
  createInitialOnboardingState,
  type ChecklistTaskId,
  type OnboardingAnswers,
  type OnboardingState,
  type OnboardingStep,
  type RailStepId,
} from "./types";
import {
  descriptionForAnalytics,
  trackOnboarding,
  trackRailComplete,
  trackRailStep,
  trackWelcomeSetup,
} from "./analytics";
import { getOnboardingOverride } from "./override";

/**
 * How far into the welcome modal each step is. Only a move to a HIGHER rank is
 * a step completed: Back lowers it, and the concierge falling back to the
 * watchlists step keeps it level — that exit is reported by the concierge
 * itself (concierge_failed / concierge_rejected), not as a completion.
 */
const STEP_RANK: Record<OnboardingStep, number> = {
  profile: 0,
  concierge: 1,
  watchlists: 1,
  dashboard: 2,
};

type OnboardingStore = {
  state: OnboardingState | null;
  /** True once the initial server fetch has settled (success or failure). */
  loaded: boolean;
  /** True if the initial fetch errored — the gate stays closed in that case. */
  loadFailed: boolean;
  load: () => Promise<void>;
  /** Create + persist the initial state. Called once by the gate on trigger. */
  begin: () => void;
  setAnswers: (answers: OnboardingAnswers) => void;
  setStep: (step: OnboardingStep) => void;
  setSelectedWatchlists: (ids: number[]) => void;
  markCompleted: (appliedTemplateId: string) => void;
  dismiss: () => void;
  markTaskDone: (taskId: ChecklistTaskId) => void;
  dismissChecklist: () => void;
  /** Mint the guided run. Called once by the gate, after the welcome flow. */
  beginRail: (opts: { stepIds: RailStepId[] }) => void;
  /** Advance the run. `skipped` separates "they did it" from "they pressed Skip". */
  advanceRailStep: (opts?: { skipped?: boolean }) => void;
  exitRail: () => void;
  resumeRail: () => void;
};

let loadPromise: Promise<void> | null = null;

/**
 * Single writer for onboarding.state. All mutations merge locally then persist
 * (debounced) — avoids read-modify-write races between UI surfaces. Concurrent
 * sessions are last-write-wins on the whole key, which is acceptable here.
 */
export const useOnboardingStore = create<OnboardingStore>()((set, get) => {
  /** Merge a partial update into state and persist. */
  const update = (
    updater: (prev: OnboardingState) => OnboardingState,
    { immediate = false }: { immediate?: boolean } = {}
  ) => {
    const prev = get().state;
    if (!prev) return;
    const next = updater(prev);
    set({ state: next });
    const save = immediate ? saveOnboardingState : saveOnboardingStateDebounced;
    Promise.resolve(save(next)).catch((error) => {
      // Persistence failures shouldn't break the flow — state is still live
      // in memory; the next successful save will catch it up.
      console.error("[onboarding] Failed to save state:", error);
    });
  };

  return {
    state: null,
    loaded: false,
    loadFailed: false,

    load: () => {
      // Idempotent: many components may call this; only fetch once.
      if (!loadPromise) {
        loadPromise = (async () => {
          try {
            const state = await getOnboardingState();
            set({ state, loaded: true });
          } catch (error) {
            console.error("[onboarding] Failed to load state:", error);
            Sentry.captureException(error);
            // Fail closed: gate treats this as "don't show onboarding".
            set({ loaded: true, loadFailed: true });
          }
        })();
      }
      return loadPromise;
    },

    begin: () => {
      if (get().state) return; // already started
      const viaLink = getOnboardingOverride() !== null;
      const initial: OnboardingState = {
        ...createInitialOnboardingState(),
        ...(viaLink && { startedVia: "link" as const }),
      };
      set({ state: initial });
      trackWelcomeSetup("started", viaLink ? "link" : "self");
      saveOnboardingState(initial).catch((error) => {
        console.error("[onboarding] Failed to save initial state:", error);
      });
    },

    setAnswers: (answers) =>
      update((prev) => ({ ...prev, answers })),

    /**
     * Tracks what was on the step being LEFT, read from state — the modal
     * persists its answers and watchlist selection before calling this, so
     * they are current here.
     */
    setStep: (step) => {
      const current = get().state;
      const from = current?.lastStep ?? "profile";
      if (current && STEP_RANK[step] > STEP_RANK[from]) {
        trackWelcomeSetup("step_completed", from);
        const text = current.answers?.freeText?.trim();
        if (from === "profile" && text) {
          trackWelcomeSetup("description_submitted", descriptionForAnalytics(text));
        }
        if (from === "watchlists") {
          trackWelcomeSetup(
            "watchlists_selected",
            String(current.selectedWatchlistIds?.length ?? 0)
          );
        }
      }
      update((prev) => ({ ...prev, lastStep: step }));
    },

    // Untracked: the modal calls this on Back and again on completion, so a
    // count taken here was double on the manual route and a stray 0 on the
    // concierge one. setStep reports it once, on the way forward.
    setSelectedWatchlists: (ids) =>
      update((prev) => ({ ...prev, selectedWatchlistIds: ids })),

    markCompleted: (appliedTemplateId) => {
      trackWelcomeSetup("completed", appliedTemplateId);
      update(
        (prev) => ({
          ...prev,
          completedAt: new Date().toISOString(),
          appliedTemplateId,
        }),
        { immediate: true }
      );
    },

    dismiss: () => {
      trackWelcomeSetup("skipped", get().state?.lastStep);
      update(
        (prev) => ({ ...prev, dismissedAt: new Date().toISOString() }),
        { immediate: true }
      );
    },

    markTaskDone: (taskId) => {
      const tasks = get().state?.checklist?.tasks;
      if (!tasks || tasks[taskId]) return; // not started, or already done
      trackOnboarding("checklist_task_done", taskId);
      update((prev) => ({
        ...prev,
        checklist: {
          ...prev.checklist,
          tasks: {
            ...prev.checklist.tasks,
            [taskId]: new Date().toISOString(),
          },
        },
      }));
    },

    dismissChecklist: () => {
      trackOnboarding("checklist_dismissed");
      update(
        (prev) => ({
          ...prev,
          checklist: {
            ...prev.checklist,
            dismissedAt: new Date().toISOString(),
          },
        }),
        { immediate: true }
      );
    },

    beginRail: ({ stepIds }) => {
      const current = get().state;
      // No state = onboarding never ran; a rail already present = idempotent.
      if (!current || current.rail) return;
      update((prev) => ({
        ...prev,
        rail: {
          stepIds,
          step: 0,
          completedStepIds: [],
          // An empty run is already over. Latching here rather than letting the
          // rail mount and render nothing — a shell with no steps in it reads
          // as a broken guide, not an absent one.
          ...(stepIds.length === 0 && {
            completedAt: new Date().toISOString(),
          }),
        },
      }));
    },

    /**
     * Accounting copied in shape from the demo guide's advanceStep, because the
     * ways it can go wrong are all silent: a step counted on a Skip, an index
     * that disagrees with the total, a completion reporting four steps done
     * when one was. None of them throw and all of them read as a healthier
     * funnel than we have.
     */
    advanceRailStep: (opts) => {
      const current = get().state;
      // Guard BEFORE computing analytics. The detection subscriptions are live
      // from mount, so an event can fire before the server read lands — at
      // which point there is no rail on screen and nothing to advance.
      const rail = current?.rail;
      if (!rail || rail.completedAt) return;

      const stepId = rail.stepIds[rail.step];
      const next = rail.step + 1;
      const finished = next >= rail.stepIds.length;

      const alreadyReported =
        !!stepId && rail.completedStepIds.includes(stepId);
      const completed = !opts?.skipped && !!stepId && !alreadyReported;

      /**
       * Every step emits exactly one row, skipped or not.
       *
       * Emitting only on completion left the funnel with invisible holes:
       * someone who skipped step 2 never appeared at step 2 at all, so "how
       * many reached step 3" undercounted unless two streams were joined by
       * hand. One stream with a flag tells the whole story.
       */
      if (stepId) {
        trackRailStep({
          stepId,
          stepIndex: rail.step,
          stepTotal: rail.stepIds.length,
          skipped: !!opts?.skipped,
        });
      }

      const completedStepIds = completed
        ? [...rail.completedStepIds, stepId]
        : rail.completedStepIds;

      if (finished) {
        trackRailComplete({
          stepsCompleted: completedStepIds.length,
        });
      }

      update(
        (prev) => ({
          ...prev,
          rail: {
            ...prev.rail!,
            step: next,
            completedStepIds,
            ...(finished && { completedAt: new Date().toISOString() }),
          },
        }),
        // Immediate on the last step. The debounce is 2s leading-edge, so a
        // user who finishes and closes the tab inside that window would lose
        // completedAt and be handed the whole rail again next session.
        { immediate: finished }
      );
    },

    exitRail: () => {
      const rail = get().state?.rail;
      if (!rail || rail.completedAt || rail.exitedAt) return;
      trackOnboarding("rail_exited", rail.stepIds[rail.step]);
      update(
        (prev) => ({
          ...prev,
          rail: { ...prev.rail!, exitedAt: new Date().toISOString() },
        }),
        // A user-visible decision: it has to survive a reload.
        { immediate: true }
      );
    },

    resumeRail: () => {
      const rail = get().state?.rail;
      if (!rail?.exitedAt) return;
      trackOnboarding("rail_resumed", rail.stepIds[rail.step]);
      update((prev) => ({
        ...prev,
        rail: { ...prev.rail!, exitedAt: undefined },
      }));
    },
  };
});
